import React, { createContext, useContext, useEffect, useState } from 'react'

export enum ActiveSelectField {
  ROUTE = 'ROUTE',
  VEHICLE = 'VEHICLE',
  PASSENGER = 'PASSENGER',
  DATE = 'DATE',
  CURRENCY = 'CURRENCY',
  MULTI_OFFER = 'MULTI_OFFER',
  NONE = 'NONE',
}

type ActiveSelectFieldContextType = {
  activeField: ActiveSelectField
  closeIfActive: (field: ActiveSelectField) => void
  setActiveField: (field: ActiveSelectField) => void
  setTravelSearchInViewport: (value: boolean) => void
  toggleField: (field: ActiveSelectField, clickedOutside: boolean) => void
  travelSearchInViewport: boolean
}

const ActiveSelectFieldContext = createContext<ActiveSelectFieldContextType | undefined>(undefined)

export const ActiveSelectFieldProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [activeField, setActiveField] = useState<ActiveSelectField>(ActiveSelectField.NONE)
  const [travelSearchInViewport, setTravelSearchInViewport] = useState<boolean>(false)

  const toggleField = (field: ActiveSelectField, clickedOutside: boolean) => {
    setActiveField(clickedOutside ? ActiveSelectField.NONE : field)
  }

  const closeIfActive = (field: ActiveSelectField) => {
    if (activeField === field) {
      setActiveField(ActiveSelectField.NONE)
    }
  }

  useEffect(() => {
    const handleEsc = (event: any) => {
      if (event.key === 'Escape') {
        setActiveField(ActiveSelectField.NONE)
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  // Tabbing onto another selector's trigger leaves the open panel behind, so dismiss it.
  useEffect(() => {
    if (activeField === ActiveSelectField.NONE) return

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null
      const trigger = target?.closest<HTMLElement>('[id$="-trigger"]')
      if (!trigger || trigger.id === `${activeField.toLowerCase()}-trigger`) return
      setActiveField(ActiveSelectField.NONE)
    }

    window.addEventListener('focusin', handleFocusIn)
    return () => window.removeEventListener('focusin', handleFocusIn)
  }, [activeField])

  return (
    <ActiveSelectFieldContext.Provider
      value={{
        activeField,
        closeIfActive,
        setActiveField,
        setTravelSearchInViewport,
        toggleField,
        travelSearchInViewport,
      }}
    >
      {children}
    </ActiveSelectFieldContext.Provider>
  )
}

export const useActiveSelectField = () => {
  const context = useContext(ActiveSelectFieldContext)
  if (!context) throw new Error('useActiveSelectField must be used within an ActiveSelectFieldProvider')
  return context
}
